import { AxleEvent } from '@/components/calendars/AxleCalendar';
import { IClinicianAppointment } from '@/components/liveView/LiveView';
import { VisitOutcome, VisitState } from '@/types/visit';

import { EventStatus } from './status';

// Each row is 180px + 30px padding = 210px total
export const ROW_HEIGHT = 210;
// If you want more events, increase the height above as well as this number
export const MAX_WINDOW_EVENTS = 2;

export type ServicesForCardDisplay = {
    id: string;
    name: string;
};

// Minimal clinician shape the calendar renders (first/last name only). Both the episode
// query's ClinicianOptionFragment and the unified-visit clinician chip satisfy it.
export type CalendarEventClinician = {
    firstName: string;
    lastName: string;
};

export enum DisciplineColors {
    PRIMARY_200 = '#99F6E4',
    SECONDARY_200 = '#A7F3D0',
    TERTIARY_200 = '#E2E8F0',
    TEAL_200 = '#B2DFDB',
    RED_200 = '#FECACA',
    YELLOW_200 = '#FDE68A',
    GREEN_200 = '#BBF7D0',
    BLUE_200 = '#BFDBFE',
    INDIGO_200 = '#C7D2FE',
    PURPLE_200 = '#DDD6FE',
    PINK_200 = '#FBCFE8',
}

export const DISCIPLINE_COLOR_PALETTE = Object.values(DisciplineColors);

export interface ICalendarVisit {
    __typename: 'Visit';
    id: string;
    effectiveDate: Date;
    state: VisitState;
    outcome: VisitOutcome;
    timezone: string;
    disciplines: { id: string; code: string }[];
    clinicianAppointments:
        | (Omit<IClinicianAppointment, 'durationMinutes'> & {
              services: ServicesForCardDisplay[];
          })[]
        | null;
}

// Fields for a visit card in a day cell (the visit's data, minus discriminant bookkeeping).
export type VisitTileFields = Omit<AxleEvent, 'resourceId'> &
    Omit<ICalendarVisit, 'clinicianAppointments'> & {
        status: EventStatus;
        appointment: {
            id: string;
            startDatetime: Date | null;
            endDatetime: Date | null;
            clinician: CalendarEventClinician | null;
            services: ServicesForCardDisplay[];
        };
    };

// Domain-neutral event model: a tile is a visit card in one day cell; a bar is an all-day
// labeled span (color + label from the caller). The engine attaches no domain meaning.
export type CalendarTile = { kind: 'tile' } & VisitTileFields;

export type CalendarBar = {
    kind: 'bar';
    id: string;
    start: Date;
    end: Date;
    label: string;
    color: string;
};

export type CalendarEvent = CalendarTile | CalendarBar;

// A single-day badge on the day-number header (e.g. a span-boundary chevron); color/label/
// shape from the caller. shortLabel is used when the column is too narrow.
export type CalendarDayMarker = {
    id: string;
    date: Date;
    label: string;
    color: string;
    shortLabel?: string;
    shape?: 'start' | 'end' | 'plain';
};

// Internal: the "+N More" tile the engine synthesises when a day has too many tiles.
export type CalendarOverflow = {
    kind: 'overflow';
    start: Date;
    end: Date;
    title: string;
    uuid: string;
    dayEvents: CalendarTile[];
    hiddenCount: number;
};

// What react-big-calendar consumes: start/end/title/uuid + allDay (bars span the all-day
// row; tiles and overflow sit in one day cell).
export type EngineCalendarEvent =
    | (CalendarTile & { allDay: false })
    | (CalendarBar & { allDay: true; title: string; uuid: string })
    | (CalendarOverflow & { allDay: false });
